import { createFileRoute, Navigate, Outlet, useRouterState } from '@tanstack/react-router';

import type {
  CrmCustomerLearningSection,
  CrmCustomerLearningSort,
} from '@features/crm-customer-learning';
import { normalizeCrmCourseProfileReturnTo } from '@features/crm-course-profile';
import { CrmCustomerLearningPage } from '@pages/crm';
import { useCrmCustomerRecordRouteGuard } from '@widgets/crm-customer-record-shell';

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/learning'
)({
  component: Outlet,
});

function defaultSort(section: CrmCustomerLearningSection): CrmCustomerLearningSort {
  if (section === 'registrations') return '-submittedAt';
  if (section === 'certifications') return 'certifiedAt.desc';
  if (section === 'attempts') return 'attemptDate.desc';
  return '-courseStartAt';
}

export function CrmCustomerLearningRoute() {
  const navigate = Route.useNavigate();
  const { userProfileId: customerId, locale } = Route.useParams();
  const search = Route.useSearch();
  const currentLocation = useRouterState({ select: (state) => state.location.href });
  const fallback = useCrmCustomerRecordRouteGuard('learning');
  if (fallback !== null)
    return (
      <Navigate
        to={fallback.route}
        params={{ userProfileId: customerId, locale }}
        search={search}
        replace
        resetScroll={false}
      />
    );

  const filters = {
    ...(search.learningCertificationTypeId === undefined
      ? {}
      : { certificationTypeId: search.learningCertificationTypeId }),
    ...(search.learningCertifiedFrom === undefined
      ? {}
      : { certifiedFrom: search.learningCertifiedFrom }),
    ...(search.learningCertifiedTo === undefined
      ? {}
      : { certifiedTo: search.learningCertifiedTo }),
    ...(search.learningAttemptedFrom === undefined
      ? {}
      : { attemptedFrom: search.learningAttemptedFrom }),
    ...(search.learningAttemptedTo === undefined
      ? {}
      : { attemptedTo: search.learningAttemptedTo }),
    ...(search.learningCostMax === undefined ? {} : { costMax: search.learningCostMax }),
    ...(search.learningCostMin === undefined ? {} : { costMin: search.learningCostMin }),
    ...(search.learningCourseId === undefined ? {} : { courseId: search.learningCourseId }),
    ...(search.learningCourseStatus === undefined
      ? {}
      : { courseStatus: search.learningCourseStatus }),
    ...(search.learningCourseTypeId === undefined
      ? {}
      : { courseTypeId: search.learningCourseTypeId }),
    ...(search.learningInstructorId === undefined
      ? {}
      : { instructorId: search.learningInstructorId }),
    ...(search.learningInstructorPaymentStatus === undefined
      ? {}
      : { instructorPaymentStatus: search.learningInstructorPaymentStatus }),
    ...(search.learningDateFrom === undefined ? {} : { dateFrom: search.learningDateFrom }),
    ...(search.learningDateTo === undefined ? {} : { dateTo: search.learningDateTo }),
    ...(search.learningItemId === undefined ? {} : { learningItemId: search.learningItemId }),
    ...(search.learningRegistrationId === undefined
      ? {}
      : { registrationId: search.learningRegistrationId }),
    ...(search.learningRegistrationReferenceType === undefined
      ? {}
      : { registrationReferenceType: search.learningRegistrationReferenceType }),
    ...(search.learningRegistrationType === undefined
      ? {}
      : { registrationType: search.learningRegistrationType }),
    ...(search.learningResult === undefined ? {} : { result: search.learningResult }),
    ...(search.learningSchoolId === undefined ? {} : { schoolId: search.learningSchoolId }),
    ...(search.learningStudentPaymentStatus === undefined
      ? {}
      : { studentPaymentStatus: search.learningStudentPaymentStatus }),
    ...(search.learningSubmittedFrom === undefined
      ? {}
      : { submittedFrom: search.learningSubmittedFrom }),
    ...(search.learningSubmittedTo === undefined
      ? {}
      : { submittedTo: search.learningSubmittedTo }),
    ...(search.learningType === undefined ? {} : { type: search.learningType }),
  };

  const section = search.learningSection ?? 'courses';
  const sort = search.learningSort ?? defaultSort(section);

  return (
    <CrmCustomerLearningPage
      attemptCursor={search.learningCursor}
      attemptCursorHistory={search.learningCursorHistory}
      filters={filters}
      onAttemptCursorChange={(learningCursor, learningCursorHistory) => {
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            learningCursor,
            learningCursorHistory:
              learningCursorHistory === undefined || learningCursorHistory.length === 0
                ? undefined
                : learningCursorHistory,
            learningPage: undefined,
          }),
        });
      }}
      page={search.learningPage ?? 1}
      q={search.learningQ}
      section={section}
      sort={sort}
      onFiltersChange={(nextFilters) => {
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            learningCertificationTypeId: nextFilters.certificationTypeId,
            learningCertifiedFrom: nextFilters.certifiedFrom,
            learningCertifiedTo: nextFilters.certifiedTo,
            learningAttemptedFrom: nextFilters.attemptedFrom,
            learningAttemptedTo: nextFilters.attemptedTo,
            learningCostMax: nextFilters.costMax,
            learningCostMin: nextFilters.costMin,
            learningCourseId: nextFilters.courseId,
            learningCourseStatus: nextFilters.courseStatus,
            learningCourseTypeId: nextFilters.courseTypeId,
            learningDateFrom: nextFilters.dateFrom,
            learningDateTo: nextFilters.dateTo,
            learningInstructorId: nextFilters.instructorId,
            learningInstructorPaymentStatus: nextFilters.instructorPaymentStatus,
            learningItemId: nextFilters.learningItemId,
            learningCursor: undefined,
            learningCursorHistory: undefined,
            learningPage: undefined,
            learningRegistrationId: nextFilters.registrationId,
            learningRegistrationReferenceType: nextFilters.registrationReferenceType,
            learningRegistrationType: nextFilters.registrationType,
            learningResult: nextFilters.result,
            learningSchoolId: nextFilters.schoolId,
            learningStudentPaymentStatus: nextFilters.studentPaymentStatus,
            learningSubmittedFrom: nextFilters.submittedFrom,
            learningSubmittedTo: nextFilters.submittedTo,
            learningSection:
              nextFilters.registrationId !== undefined || nextFilters.learningItemId !== undefined
                ? 'attempts'
                : current.learningSection,
            learningSort: undefined,
            learningType: nextFilters.type,
          }),
        });
      }}
      onOpenRegistrationProfile={(registrationId) => {
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/learning/registrations/$registrationId',
          params: { userProfileId: customerId, locale, registrationId },
          search: (current) => ({ ...current, registrationProfileTab: undefined }),
        });
      }}
      onOpenCertificationProfile={(certificationId) => {
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/learning/certifications/$certificationId',
          params: { certificationId, userProfileId: customerId, locale },
          search: (current) => ({ ...current, certificationProfileTab: undefined }),
        });
      }}
      onOpenEducationModuleLogProfile={(educationModuleLogId) => {
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/learning/education-module-logs/$educationModuleLogId',
          params: { userProfileId: customerId, educationModuleLogId, locale },
          search: (current) => ({ ...current, educationModuleLogProfileTab: undefined }),
        });
      }}
      onViewCourseRegistrations={(courseId) => {
        void navigate({
          search: (current) => ({
            ...current,
            learningCertificationTypeId: undefined,
            learningCertifiedFrom: undefined,
            learningCertifiedTo: undefined,
            learningAttemptedFrom: undefined,
            learningAttemptedTo: undefined,
            learningCostMax: undefined,
            learningCostMin: undefined,
            learningCourseId: courseId,
            learningCourseStatus: undefined,
            learningCourseTypeId: undefined,
            learningDateFrom: undefined,
            learningDateTo: undefined,
            learningInstructorId: undefined,
            learningInstructorPaymentStatus: undefined,
            learningItemId: undefined,
            learningCursor: undefined,
            learningCursorHistory: undefined,
            learningPage: undefined,
            learningQ: undefined,
            learningRegistrationId: undefined,
            learningRegistrationReferenceType: undefined,
            learningRegistrationType: undefined,
            learningResult: undefined,
            learningSchoolId: undefined,
            learningSection: 'registrations',
            learningSort: undefined,
            learningStudentPaymentStatus: undefined,
            learningSubmittedFrom: undefined,
            learningSubmittedTo: undefined,
            learningType: undefined,
          }),
        });
      }}
      onViewCourseProfile={(courseId) => {
        void navigate({
          to: '/$locale/crm/courses/$courseId',
          params: { courseId, locale },
          search: {
            returnTo: normalizeCrmCourseProfileReturnTo(currentLocation),
          },
        });
      }}
      onViewRegistrationDocuments={(documentRegistrationId) => {
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/documents',
          params: { userProfileId: customerId, locale },
          search: (current) => ({
            ...current,
            documentFileState: undefined,
            documentPage: undefined,
            documentQ: undefined,
            documentRegistrationId,
            documentSelectedId: undefined,
            documentSort: undefined,
            documentStatus: undefined,
            documentType: undefined,
          }),
        });
      }}
      onViewRegistration={(registrationNumber) => {
        void navigate({
          search: (current) => ({
            ...current,
            learningCertificationTypeId: undefined,
            learningCertifiedFrom: undefined,
            learningCertifiedTo: undefined,
            learningAttemptedFrom: undefined,
            learningAttemptedTo: undefined,
            learningCostMax: undefined,
            learningCostMin: undefined,
            learningCourseId: undefined,
            learningCourseStatus: undefined,
            learningCourseTypeId: undefined,
            learningDateFrom: undefined,
            learningDateTo: undefined,
            learningInstructorId: undefined,
            learningInstructorPaymentStatus: undefined,
            learningItemId: undefined,
            learningCursor: undefined,
            learningCursorHistory: undefined,
            learningPage: undefined,
            learningQ: registrationNumber,
            learningRegistrationId: undefined,
            learningRegistrationReferenceType: undefined,
            learningRegistrationType: undefined,
            learningResult: undefined,
            learningSchoolId: undefined,
            learningSection: 'registrations',
            learningSort: undefined,
            learningStudentPaymentStatus: undefined,
            learningSubmittedFrom: undefined,
            learningSubmittedTo: undefined,
            learningType: undefined,
          }),
        });
      }}
      onPageChange={(learningPage) => {
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            learningPage: learningPage === 1 ? undefined : learningPage,
          }),
        });
      }}
      onSearchChange={(learningQ) => {
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            learningCursor: undefined,
            learningCursorHistory: undefined,
            learningPage: undefined,
            learningQ,
          }),
        });
      }}
      onSectionChange={(learningSection) => {
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            learningCertificationTypeId: undefined,
            learningCertifiedFrom: undefined,
            learningCertifiedTo: undefined,
            learningAttemptedFrom: undefined,
            learningAttemptedTo: undefined,
            learningCostMax: undefined,
            learningCostMin: undefined,
            learningCourseId: undefined,
            learningCourseStatus: undefined,
            learningCourseTypeId: undefined,
            learningDateFrom: undefined,
            learningDateTo: undefined,
            learningInstructorId: undefined,
            learningInstructorPaymentStatus: undefined,
            learningItemId: undefined,
            learningCursor: undefined,
            learningCursorHistory: undefined,
            learningPage: undefined,
            learningQ: undefined,
            learningRegistrationId: undefined,
            learningRegistrationReferenceType: undefined,
            learningRegistrationType: undefined,
            learningResult: undefined,
            learningSchoolId: undefined,
            learningSection: learningSection === 'courses' ? undefined : learningSection,
            learningSort: undefined,
            learningStudentPaymentStatus: undefined,
            learningSubmittedFrom: undefined,
            learningSubmittedTo: undefined,
            learningType: undefined,
          }),
        });
      }}
      onSortChange={(learningSort) => {
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            learningCursor: undefined,
            learningCursorHistory: undefined,
            learningPage: undefined,
            learningSort:
              learningSort === defaultSort(current.learningSection ?? 'courses')
                ? undefined
                : learningSort,
          }),
        });
      }}
    />
  );
}
